import * as z from 'zod'

const enum Period {
  year = 0,
  month = 1,
}

export class GoalPeriod {
  private _intValue: Period
  constructor(init: Period | 'year' | 'month' | 0 | 1) {
    if (typeof init === 'number') {
      if (init === 0 || init === 1) {
        this._intValue = init
      } else {
        throw new Error('Invalid init')
      }
    } else {
      if (init === 'year') {
        this._intValue = Period.year
      } else if (init === 'month') {
        this._intValue = Period.month
      } else {
        throw new Error('Invalid init')
      }
    }
  }
  static year = Period.year
  static month = Period.month
  get intValue() {
    return this._intValue
  }
  get noun() {
    switch (this._intValue) {
      case Period.year:
        return 'year'
      case Period.month:
        return 'month'
      default:
        throw new Error('Invalid period')
    }
  }
  set noun(value) {
    switch (value) {
      case 'year':
        this._intValue = Period.year
        break
      case 'month':
        this._intValue = Period.month
        break
      default:
        throw new Error(`Unknown period: ${value}`)
    }
  }
  get adverb() {
    switch (this._intValue) {
      case Period.year:
        return 'yearly'
      case Period.month:
        return 'monthly'
      default:
        throw new Error('Invalid period')
    }
  }
  set adverb(value: 'yearly' | 'monthly') {
    switch (value) {
      // case 'annually':
      case 'yearly':
        this._intValue = Period.year
        break
      case 'monthly':
        this._intValue = Period.month
        break
      default:
        throw new Error(`Unknown period: ${value}`)
    }
  }
  get key(): 'this_year' | 'this_month' {
    return `this_${this.noun}`
  }
}

const StringyDate = z
  .union([z.string(), z.number(), z.date()])
  .transform((val) => new Date(val))

type StringyDate = z.input<typeof StringyDate>

export const ApiGoal = z.object({
  id: z.number(),
  created_at: StringyDate,
  updated_at: StringyDate,
  goal_period: z.enum(['month', 'year']),
  target_value: z.number(),
})
export type ApiGoal = z.infer<typeof ApiGoal>

export const NeedsMetStats = z.object({
  all_time: z.number(),
  this_year: z.number(),
  this_month: z.number(),
  all_time_value: z.number().nullable(),
})
export type NeedsMetStats = z.infer<typeof NeedsMetStats>

export class Goal {
  id?: number
  created_at: Date
  updated_at: Date
  period: GoalPeriod

  constructor(
    private apiGoal: ApiGoal,

    private needsMetStats: NeedsMetStats
  ) {
    const parsedApiGoal = ApiGoal.parse(apiGoal)
    this.apiGoal = parsedApiGoal
    const parsedNeedsMetStats = NeedsMetStats.parse(needsMetStats)
    this.needsMetStats = parsedNeedsMetStats
    this.id = parsedApiGoal.id
    this.created_at = parsedApiGoal.created_at
    this.updated_at = parsedApiGoal.updated_at
    this.period = new GoalPeriod(parsedApiGoal.goal_period)
  }

  get currentValue(): number {
    return this.needsMetStats[this.period.key]
  }

  get targetValue(): number {
    return this.apiGoal.target_value
  }

  asLegacyFormat(): ApiGoal & {
    period: GoalPeriod
  } {
    return {
      ...this.apiGoal,
      period: this.period,
    }
  }
  static formatForGoalMutation = ({
    period,
    targetValue,
  }: {
    period: GoalPeriod
    targetValue: number
  }) => ({
    regularity: period.intValue,
    number: String(targetValue),
  })
}
